import { LitElement, html, css, svg } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../api';
import { store } from '../../store';
import { t } from '../../i18n';
import { money } from '../../format';
import { showToast } from '../../components/ui/toast';

type Period = 'month' | 'lastMonth' | 'quarter' | 'lastQuarter' | 'year' | 'lastYear' | 'all';

interface CountRow { label: string; count: number }
interface CategoryRow { category: string; amount: number }
interface MonthlyNetRow { month: string; income: number; expense: number; net: number }
interface RetentionWindow { window: '3m' | '6m' | '12m'; rate: number | null; activeThen: number; retained: number }
interface TrendPoint { month: string; active: number }

interface FinancialReport {
  summary: { primary_currency: string; income: number; expense: number; net: number; outstanding: number; outstanding_indicative: boolean };
  incomeByCategory: CategoryRow[];
  expenseByCategory: CategoryRow[];
  monthlyNet: MonthlyNetRow[];
}
interface OutstandingReport {
  primary_currency: string; pending: number; overdue: number; total: number;
  rows: { id: string; student: string; fee: string; amount: number; currency: string; status: string; due_date: string | null; period: string | null }[];
}
interface StudentReport {
  trend: TrendPoint[];
  retention: RetentionWindow[];
  byLevel: CountRow[]; byDanceType: CountRow[]; byClass: CountRow[];
}
interface TeacherPayRow {
  teacher_id: string; name: string; pay_type: string | null; pay_rate: number | null;
  pay_currency: string | null; sessions: number; amount: number | null; amount_primary: number | null;
}
interface TeacherPay { primary_currency: string; rows: TeacherPayRow[]; total_primary: number; months: number | null }

/** Reports (Phase 5.6). Financial and student reports over a fixed period. Financial
 *  figures are in the school's PRIMARY currency; sections render per the viewer's
 *  permissions (financial → accounting.view, students → students.view). CSV export
 *  gated by accounting.export (financial/outstanding) / students.view (students). */
@customElement('cadence-reporting-page')
export class CadenceReportingPage extends LitElement {
  @state() private loading = true;
  @state() private period: Period = 'month';
  @state() private financial: FinancialReport | null = null;
  @state() private outstanding: OutstandingReport | null = null;
  @state() private students: StudentReport | null = null;
  @state() private teacherPay: TeacherPay | null = null;

  private canAccounting = false;
  private canStudents = false;
  private canExport = false;

  static styles = css`
    :host { display: block; }
    h1 { margin: 0 0 4px; font-size: 1.6rem; }
    h2 { margin: var(--space-5) 0 var(--space-3); font-size: 1.1rem; display: flex; align-items: center; gap: 10px; }
    h2 .ex { margin-left: auto; }
    p.sub { margin: 0 0 var(--space-4); color: var(--color-text-secondary); }
    .periods { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: var(--space-4); }
    .periods button {
      font: inherit; font-size: 0.85rem; padding: 6px 12px; border-radius: var(--radius-md);
      border: 1px solid var(--color-border); background: var(--color-bg-primary); color: var(--color-text-primary); cursor: pointer;
    }
    .periods button[aria-pressed='true'] { background: var(--color-primary); color: #fff; border-color: var(--color-primary); }
    .cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: var(--space-4); margin-bottom: var(--space-4); }
    .value { font-size: 1.5rem; font-weight: 700; }
    .value.income { color: var(--color-success); }
    .value.expense { color: var(--color-danger); }
    .value.net.neg { color: var(--color-danger); }
    .sub { color: var(--color-text-secondary); font-size: 0.72rem; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-4); align-items: stretch; }
    @media (max-width: 820px) { .grid { grid-template-columns: 1fr; } }
    .break { display: flex; flex-direction: column; gap: 2px; }
    .break .row { display: flex; justify-content: space-between; gap: 10px; padding: 7px 0; border-bottom: 1px solid var(--color-border); font-size: 0.9rem; }
    .break .row:last-child { border-bottom: none; }
    .pos { color: var(--color-success); }
    .neg { color: var(--color-danger); }
    .muted { color: var(--color-text-secondary); }
    .spark svg { display: block; width: 100%; height: 60px; }
    .spark.fill { height: 100%; min-height: 60px; }
    .spark.fill svg { height: 100%; }
    .retention { display: flex; gap: var(--space-4); flex-wrap: wrap; }
    .retention .r { flex: 1; min-width: 90px; text-align: center; }
    .retention .r .pct { font-size: 1.6rem; font-weight: 700; }
    .retention .r .lbl { color: var(--color-text-secondary); font-size: 0.78rem; margin-top: 2px; }
    .retention .r .meta { color: var(--color-text-secondary); font-size: 0.7rem; }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    this.canAccounting = store.hasPermission('accounting.view');
    this.canStudents = store.hasPermission('students.view');
    this.canExport = store.hasPermission('accounting.export');
    this.load();
  }

  /** Map the selected fixed period to an inclusive [from,to] date range (YYYY-MM-DD). */
  private range(): { from?: string; to?: string } {
    const now = new Date();
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    const y = now.getFullYear(); const m = now.getMonth();
    const q = Math.floor(m / 3); // 0-3
    switch (this.period) {
      case 'month': return { from: iso(new Date(y, m, 1)), to: iso(now) };
      case 'lastMonth': return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
      case 'quarter': return { from: iso(new Date(y, q * 3, 1)), to: iso(now) };
      case 'lastQuarter': return { from: iso(new Date(y, q * 3 - 3, 1)), to: iso(new Date(y, q * 3, 0)) };
      case 'year': return { from: iso(new Date(y, 0, 1)), to: iso(now) };
      case 'lastYear': return { from: iso(new Date(y - 1, 0, 1)), to: iso(new Date(y - 1, 11, 31)) };
      default: return {};
    }
  }

  private qs(): string {
    const { from, to } = this.range();
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    return qs.toString();
  }

  private async load() {
    this.loading = true;
    const suffix = this.qs() ? `?${this.qs()}` : '';
    try {
      const tasks: Promise<unknown>[] = [];
      if (this.canAccounting) {
        tasks.push(api.get<FinancialReport>(`/reporting/financial${suffix}`).then((r) => (this.financial = r)));
        tasks.push(api.get<OutstandingReport>('/reporting/outstanding').then((r) => (this.outstanding = r)));
        tasks.push(api.get<TeacherPay>(`/accounting/teacher-pay${suffix}`).then((r) => (this.teacherPay = r)));
      }
      if (this.canStudents) {
        tasks.push(api.get<StudentReport>('/reporting/students').then((r) => (this.students = r)));
      }
      await Promise.all(tasks);
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private setPeriod(p: Period) { if (p !== this.period) { this.period = p; this.load(); } }

  private exportCsv(kind: 'financial' | 'outstanding' | 'students') {
    const suffix = kind === 'financial' && this.qs() ? `?${this.qs()}` : '';
    window.location.href = `/api/v1/reporting/export/${kind}.csv${suffix}`;
  }

  private get primary() { return this.financial?.summary.primary_currency || this.outstanding?.primary_currency || 'USD'; }

  render() {
    if (this.loading && !this.financial && !this.students) return html`<cadence-loading></cadence-loading>`;
    return html`
      <h1>${t('reporting.title')}</h1>
      <p class="sub">${t('reporting.subtitle')}</p>

      <div class="periods">
        ${(['month', 'lastMonth', 'quarter', 'lastQuarter', 'year', 'lastYear', 'all'] as Period[]).map(
          (p) => html`<button aria-pressed=${this.period === p ? 'true' : 'false'} @click=${() => this.setPeriod(p)}>${t(`reporting.periods.${p}`)}</button>`,
        )}
      </div>

      ${this.canAccounting ? this.financialSection() : ''}
      ${this.canAccounting ? this.outstandingSection() : ''}
      ${this.canStudents ? this.studentsSection() : ''}
    `;
  }

  // --- Financial -----------------------------------------------------------

  private financialSection() {
    const f = this.financial;
    if (!f) return '';
    const s = f.summary;
    return html`
      <h2>
        ${t('reporting.financial.heading')}
        ${this.canExport ? html`<cadence-button class="ex" variant="ghost" @click=${() => this.exportCsv('financial')}>${t('reporting.exportCsv')}</cadence-button>` : ''}
      </h2>
      <div class="cards">
        <cadence-stat-card heading=${t('reporting.financial.income')} icon="trending-up"><div class="value income">${money(s.income, this.primary)}</div></cadence-stat-card>
        <cadence-stat-card heading=${t('reporting.financial.expenses')} icon="trending-down"><div class="value expense">${money(s.expense, this.primary)}</div></cadence-stat-card>
        <cadence-stat-card heading=${t('reporting.financial.net')} icon="chart-line"><div class="value net ${s.net < 0 ? 'neg' : ''}">${money(s.net, this.primary)}</div></cadence-stat-card>
        <cadence-stat-card heading=${t('reporting.financial.outstanding')} icon="clock"><div class="value">${money(s.outstanding, this.primary)}</div>${s.outstanding_indicative ? html`<div class="sub">${t('reporting.financial.indicative')}</div>` : ''}</cadence-stat-card>
      </div>

      <div class="grid">
        <cadence-card heading=${t('reporting.financial.incomeByCategory')}>
          ${f.incomeByCategory.length
            ? html`<div class="break">${f.incomeByCategory.map((r) => html`<div class="row"><span>${r.category}</span><span class="pos">${money(r.amount, this.primary)}</span></div>`)}</div>`
            : html`<p class="muted">${t('reporting.financial.noIncome')}</p>`}
        </cadence-card>
        <cadence-card heading=${t('reporting.financial.expenseByCategory')}>
          ${f.expenseByCategory.length
            ? html`<div class="break">${f.expenseByCategory.map((r) => html`<div class="row"><span>${r.category}</span><span class="neg">${money(r.amount, this.primary)}</span></div>`)}</div>`
            : html`<p class="muted">${t('reporting.financial.noExpenses')}</p>`}
        </cadence-card>
      </div>

      <cadence-card heading=${t('reporting.financial.netOverTime')} style="display:block;margin-top:var(--space-4)">
        ${f.monthlyNet.length
          ? html`
            <div class="spark">${this.netChart(f.monthlyNet)}</div>
            <cadence-table .columns=${[
              { key: 'month', label: t('reporting.financial.month') },
              { key: 'income', label: t('reporting.financial.income'), render: (r: MonthlyNetRow) => html`<span class="pos">${money(r.income, this.primary)}</span>` },
              { key: 'expense', label: t('reporting.financial.expenses'), render: (r: MonthlyNetRow) => html`<span class="neg">${money(r.expense, this.primary)}</span>` },
              { key: 'net', label: t('reporting.financial.net'), render: (r: MonthlyNetRow) => html`<span class=${r.net < 0 ? 'neg' : 'pos'}>${money(r.net, this.primary)}</span>` },
            ]} .rows=${f.monthlyNet}></cadence-table>`
          : html`<p class="muted">${t('reporting.financial.noData')}</p>`}
      </cadence-card>

      ${this.teacherPay && this.teacherPay.rows.length ? html`
        <cadence-card heading="${t('reporting.financial.heading')} · ${t('common.nav.teachers')}" style="display:block;margin-top:var(--space-4)">
          <cadence-table .columns=${[
            { key: 'name', label: t('common.nav.teachers') },
            { key: 'sessions', label: 'Sessions', render: (r: TeacherPayRow) => html`${r.pay_type === 'per_class' ? r.sessions : '—'}` },
            { key: 'amount', label: 'Pay', render: (r: TeacherPayRow) => r.amount == null ? html`<span class="muted">—</span>` : html`${money(r.amount, r.pay_currency || this.primary)}` },
            { key: 'amount_primary', label: `In ${this.primary}`, render: (r: TeacherPayRow) => (r.pay_currency || this.primary) === this.primary ? html`<span class="muted">—</span>` : (r.amount_primary == null ? html`<span class="neg">needs rate</span>` : html`${money(r.amount_primary, this.primary)}`) },
          ]} .rows=${this.teacherPay.rows}></cadence-table>
          <p class="muted" style="margin:10px 0 0">Estimated total (indicative): <strong>${money(this.teacherPay.total_primary, this.primary)}</strong></p>
        </cadence-card>` : ''}
    `;
  }

  // --- Outstanding ---------------------------------------------------------

  private outstandingSection() {
    const o = this.outstanding;
    if (!o) return '';
    return html`
      <h2>
        ${t('reporting.outstanding.heading')}
        ${this.canExport && o.rows.length ? html`<cadence-button class="ex" variant="ghost" @click=${() => this.exportCsv('outstanding')}>${t('reporting.exportCsv')}</cadence-button>` : ''}
      </h2>
      <p class="sub">
        ${t('reporting.outstanding.pending')}: ${money(o.pending, o.primary_currency)} ·
        ${t('reporting.outstanding.overdue')}: ${money(o.overdue, o.primary_currency)} ·
        ${t('reporting.outstanding.total')}: <strong>${money(o.total, o.primary_currency)}</strong>
      </p>
      ${o.rows.length
        ? html`<cadence-card style="display:block"><cadence-table .columns=${[
            { key: 'student', label: t('reporting.outstanding.student') },
            { key: 'fee', label: t('reporting.outstanding.fee') },
            { key: 'status', label: t('reporting.outstanding.status'), render: (r: OutstandingReport['rows'][number]) => html`<cadence-badge color=${r.status === 'overdue' ? 'var(--color-danger)' : 'var(--color-warning, #d97706)'}>${r.status}</cadence-badge>` },
            { key: 'due_date', label: t('reporting.outstanding.due'), render: (r: OutstandingReport['rows'][number]) => html`${(r.due_date ?? '').slice(0, 10) || '—'}` },
            { key: 'amount', label: t('reporting.outstanding.amount'), render: (r: OutstandingReport['rows'][number]) => html`${money(r.amount, r.currency)}` },
          ]} .rows=${o.rows}></cadence-table></cadence-card>`
        : html`<p class="muted">${t('reporting.outstanding.none')}</p>`}
    `;
  }

  // --- Students ------------------------------------------------------------

  private studentsSection() {
    const s = this.students;
    if (!s) return '';
    const label: Record<RetentionWindow['window'], string> = {
      '3m': t('reporting.students.retention3'), '6m': t('reporting.students.retention6'), '12m': t('reporting.students.retention12'),
    };
    return html`
      <h2>
        ${t('reporting.students.heading')}
        ${this.canStudents ? html`<cadence-button class="ex" variant="ghost" @click=${() => this.exportCsv('students')}>${t('reporting.exportCsv')}</cadence-button>` : ''}
      </h2>
      <div class="grid">
        <cadence-card heading=${t('reporting.students.countOverTime')}>
          <div class="spark fill">${this.sparkline(s.trend.map((p) => p.active))}</div>
        </cadence-card>
        <cadence-card heading=${t('reporting.students.retention')}>
          <div class="retention">
            ${s.retention.map((r) => html`
              <div class="r">
                <div class="pct">${r.rate == null ? '—' : `${r.rate}%`}</div>
                <div class="lbl">${label[r.window]}</div>
                <div class="meta">${r.retained}/${r.activeThen}</div>
              </div>`)}
          </div>
        </cadence-card>
      </div>
      <div class="grid" style="margin-top:var(--space-4)">
        ${this.countCard(t('reporting.students.byLevel'), s.byLevel)}
        ${this.countCard(t('reporting.students.byDanceType'), s.byDanceType)}
      </div>
      <cadence-card heading=${t('reporting.students.byClass')} style="display:block;margin-top:var(--space-4)">
        ${this.countList(s.byClass)}
      </cadence-card>
    `;
  }

  private countCard(heading: string, rows: CountRow[]) {
    return html`<cadence-card heading=${heading}>${this.countList(rows)}</cadence-card>`;
  }

  private countList(rows: CountRow[]) {
    return rows.length
      ? html`<div class="break">${rows.map((r) => html`<div class="row"><span>${r.label}</span><span>${r.count}</span></div>`)}</div>`
      : html`<p class="muted">${t('reporting.students.none')}</p>`;
  }

  // --- Charts (hand-rolled inline SVG, no chart lib) -----------------------

  private sparkline(values: number[]) {
    const w = 240; const h = 60; const pad = 4;
    if (values.length < 2) return html`<span class="muted">—</span>`;
    const min = Math.min(...values); const max = Math.max(...values);
    const span = max - min || 1;
    const pts = values.map((v, i) => {
      const x = pad + (i / (values.length - 1)) * (w - pad * 2);
      const y = h - pad - ((v - min) / span) * (h - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    return html`
      <svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" aria-hidden="true">
        ${svg`<polyline points=${pts} fill="none" stroke="var(--color-primary)" stroke-width="1.5" vector-effect="non-scaling-stroke" stroke-linejoin="round" stroke-linecap="round" />`}
      </svg>`;
  }

  /** Zero-baseline net bars: green above, red below. */
  private netChart(rows: MonthlyNetRow[]) {
    const w = Math.max(240, rows.length * 28); const h = 60; const pad = 4;
    const vals = rows.map((r) => r.net);
    const max = Math.max(0, ...vals); const min = Math.min(0, ...vals);
    const span = (max - min) || 1;
    const zeroY = pad + (max / span) * (h - pad * 2);
    const bw = (w - pad * 2) / rows.length;
    return html`
      <svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" aria-hidden="true">
        ${svg`<line x1="0" y1=${zeroY.toFixed(1)} x2=${w} y2=${zeroY.toFixed(1)} stroke="var(--color-border)" stroke-width="1" />`}
        ${rows.map((r, i) => {
          const x = pad + i * bw + bw * 0.15;
          const bwInner = bw * 0.7;
          const v = r.net;
          const y = v >= 0 ? pad + ((max - v) / span) * (h - pad * 2) : zeroY;
          const bh = Math.max(1, Math.abs((v / span) * (h - pad * 2)));
          const color = v >= 0 ? 'var(--color-success)' : 'var(--color-danger)';
          return svg`<rect x=${x.toFixed(1)} y=${y.toFixed(1)} width=${bwInner.toFixed(1)} height=${bh.toFixed(1)} fill=${color} rx="1"><title>${r.month}: ${r.net}</title></rect>`;
        })}
      </svg>`;
  }
}
